/* =============================================================
   AccountMenu — the person icon at the top right of every app page.

   Signed out: a link to /account (sign in or register) that brings
   the visitor back to this page afterwards. Signed in: the person's
   initials, opening a small menu with their email, "Your account"
   and "Sign out".
   ============================================================ */

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { accountHref } from '../lib/nav';


function initials(name: string | undefined, email: string | null): string {
  const src = (name || email || '?').trim();
  const parts = src.split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (name && parts[1] ? parts[1][0] : '')).toUpperCase();
}

export function AccountMenu() {
  const { user, loading, signOut } = useAuth();
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  if (loading) return <span className="pi-acct" style={{ opacity: 0 }} aria-hidden="true" />;

  if (!user) {
    return (
      <Link to={accountHref(pathname + search)} className="pi-acct" aria-label="Sign in or create an account" title="Sign in or create an account">
        <UserRound size={18} strokeWidth={1.7} aria-hidden="true" />
      </Link>
    );
  }

  return (
    <div className="pi-acct-wrap" ref={box}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${user.email ?? 'you'}`}
        className="pi-acct in"
      >
        {initials(user.user_metadata.full_name, user.email)}
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-[calc(100%+8px)] z-50 w-64 max-w-[calc(100vw-32px)] bg-paper border border-rule shadow-lg p-2">
          <div className="px-3 py-2 border-b border-rule mb-1">
            {user.user_metadata.full_name && <div className="font-editorial text-[15px] text-ink truncate">{user.user_metadata.full_name}</div>}
            <div className="font-editorial italic text-[13px] text-ink-muted truncate">{user.email}</div>
          </div>
          <Link role="menuitem" to="/account" onClick={() => setOpen(false)} className="block px-3 py-2 font-editorial text-[15px] text-ink hover:bg-paper-deep">
            Your account
          </Link>
          <Link role="menuitem" to="/account/teams" onClick={() => setOpen(false)} className="block px-3 py-2 font-editorial text-[15px] text-ink hover:bg-paper-deep">
            Your teams
          </Link>
          <button role="menuitem" type="button" onClick={() => signOut()} className="block w-full text-left px-3 py-2 font-editorial text-[15px] text-ink hover:bg-paper-deep">
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
